import type { FundChartDataItemParsed, StockChartDataItemParsed } from "@/interfaces/interfaces";
import { intervalStartDateRelativeTo } from "@/helpers/intervals";

export type PerformanceIntervals = {
    "1d": number | null;
    "1w": number | null;
    "2w": number | null;
    "1m": number | null;
    "3m": number | null;
    "6m": number | null;
    "1y": number | null;
    "2y": number | null;
    "3y": number | null;
    ytd: number | null;
    qtd: number | null;
    total: number | null;
};

export function calculatePerformanceIntervals(
    chart: (FundChartDataItemParsed | StockChartDataItemParsed)[],
    maxDate: Date | null,
    fundStartDate?: Date | null,
): PerformanceIntervals {
    const result: PerformanceIntervals = {
        "1d": null,
        "1w": null,
        "2w": null,
        "1m": null,
        "3m": null,
        "6m": null,
        "1y": null,
        "2y": null,
        "3y": null,
        ytd: null,
        qtd: null,
        total: null,
    };

    if (!chart || !chart.length) {
        return result;
    }

    // Filter chart to ignore values newer than maxDate
    const filteredChart = maxDate
        ? chart.filter((c: FundChartDataItemParsed | StockChartDataItemParsed) => c.date <= maxDate)
        : chart;

    if (!filteredChart.length) {
        return result;
    }

    const values = filteredChart.map((r) => {
        // Check if it's FundChartDataItemParsed or StockChartDataItemParsed
        return 'share_value' in r ? r.share_value : r.close;
    });

    const current_value = values[values.length - 1];
    const latest_date = filteredChart[filteredChart.length - 1].date;

    const startDates: { [key: string]: Date } = {};
    const intervals = ["1d", "1w", "2w", "1m", "3m", "6m", "1y", "2y", "3y"];
    for (const i of intervals) {
        startDates[i] = intervalStartDateRelativeTo(latest_date, i);
    }
    startDates.ytd = new Date(latest_date.getFullYear(), 0, 1);
    const currentMonth = latest_date.getUTCMonth();
    const currentQuarterStartMonth = Math.floor(currentMonth / 3) * 3;
    startDates.qtd = new Date(Date.UTC(latest_date.getUTCFullYear(), currentQuarterStartMonth, 1));

    let firstValue: number | null = null; // Store the first value for total calculation fallback

    for (let d = filteredChart.length - 1; d >= 0; d--) {
        const date = filteredChart[d].date;
        const reference_value = values[d];

        if (d === 0 && date.toISOString() === fundStartDate?.toISOString()) {
            firstValue = reference_value; // Store the very first value
        }

        // Calculate intervals based on start dates
        for (const i of intervals) {
            if (result[i as keyof PerformanceIntervals] == null && date <= startDates[i]) {
                result[i as keyof PerformanceIntervals] = current_value / reference_value - 1;
            }
        }

        if (result.ytd == null && date <= startDates.ytd) {
            result.ytd = current_value / reference_value - 1;
        }

        if (result.qtd == null && date <= startDates.qtd) {
            result.qtd = current_value / reference_value - 1;
        }

        // Calculate total performance based on fund start date or fallback date
        if (
            result.total == null &&
            ((fundStartDate != null && date < fundStartDate) || date.toISOString().substring(0, 10) < "2021-09-01")
        ) {
            result.total = current_value / reference_value - 1;
        }
    }

    // Fallback for total calculation if no suitable start date was found
    if (result.total === null && firstValue !== null) {
        result.total = current_value / firstValue - 1;
    }

    return result;
}
